import {
  init,
  track,
  identify,
  setUserId,
  reset,
} from '@amplitude/analytics-browser';
import { IIdentify } from '@amplitude/analytics-browser/lib/esm/types';

class AmplitudeService {
  private isInitialized = false;
  private isDevelopment = process.env.NEXT_PUBLIC_MODE !== 'production';

  constructor() {
    this.initialize();
  }

  private async initialize() {
    if (typeof window === 'undefined') return;

    const apiKey = process.env.NEXT_PUBLIC_AMPLITUDE_API_KEY;

    if (!apiKey) {
      console.warn('Amplitude API key is not provided');
      return;
    }

    try {
      init(apiKey, {
        autocapture: {
          pageViews: true,
          sessions: true,
          formInteractions: false,
          fileDownloads: false,
        },
        minIdLength: 1,
        serverUrl: 'https://api2.amplitude.com/2/httpapi',
        logLevel: this.isDevelopment ? 2 : 1,
      });

      this.isInitialized = true;

      if (this.isDevelopment) {
        console.log('Amplitude initialized successfully');
      }
    } catch (error) {
      if (this.isDevelopment) {
        console.error('Amplitude initialization failed:', error);
      }
    }
  }

  public logEvent({
    event,
    property,
  }: {
    event: string;
    property?: Record<string, string>;
  }) {
    if (!this.isInitialized) {
      if (this.isDevelopment) {
        console.warn('Amplitude not initialized, event not sent:', event);
      }
      return;
    }

    try {
      track(event, property);

      if (this.isDevelopment) {
        console.log('Amplitude event sent:', { event, property });
      }
    } catch (error) {
      console.error('Amplitude event failed:', error);
    }
  }

  public identifyUser(userId: string, userProperties?: IIdentify) {
    if (!this.isInitialized) return;

    try {
      setUserId(userId);

      if (userProperties) {
        identify(userProperties);
      }

      if (this.isDevelopment) {
        console.log('Amplitude user identified:', userId, userProperties);
      }
    } catch (error) {
      console.error('Amplitude identify failed:', error);
    }
  }

  public resetUser() {
    if (!this.isInitialized) return;

    try {
      reset();

      if (this.isDevelopment) {
        console.log('Amplitude user reset');
      }
    } catch (error) {
      console.error('Amplitude reset failed:', error);
    }
  }
}

export const amplitudeService = new AmplitudeService();
